/// <amd-module name="Build/DesignerExtensions/ExtendsCheckPanel/Components/ExtendsCheckComponent" />
import React = require("react");
import { IExtendsCheck, IExtendsCheckConfig, IExtendsCheckConfiguration, extendsCheckTypeId, extendsCheckTypeName } from "Build/DesignerExtensions/ExtendsCheckPanel/Components/ExtendsCheckConfig";
import { ExtendsCheckEditPanel } from "Build/DesignerExtensions/ExtendsCheckPanel/Components/ExtendsCheckEditPanel";
import * as PipelineChecksContracts from "Checks/Client/Contracts/PipelinesChecks";
import { CheckAction } from "Checks/Client/Contracts/Contracts";
import * as Resources from "Build/DesignerExtensions/ExtendsCheckPanel/Resources";
import { ObservableValue, ObservableArray } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssComponentProperties, IVssComponentContext, IVssLayoutManager, VssComponent, css } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { IListItemDetails, List, ListItem } from "VSSUI/List";
import { Image } from "VSSUI/Image";
import { ITableRow } from "VSSUI/Table";

interface IProps extends IVssComponentProperties {
    resource: PipelineChecksContracts.Resource;
    checkAction: CheckAction;
    extendsChecks: IExtendsCheck[];
    updateCheckDataAndEnableSave: (check: PipelineChecksContracts.CheckConfiguration) => void;
    updateTelemetryData: (telemetryData: { [key: string ]: string }) => void;
    disableCreateButton: (value: boolean) => void;
}

interface IRepositoryType {
    id: string;
    name: string;
    iconClass: string;
    iconAssetLocation: string | undefined;
    iconAsset: string | undefined;
}

export abstract class ExtendsCheckComponent extends VssComponent<IProps, {}>{
    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);
        this._pageContext = context.pageContext;
        this._extendsObservable = new ObservableArray<IExtendsCheck>([]);

        if (this.props.extendsChecks) {
            this._extendsObservable.value = this.props.extendsChecks;
        }
    }

    public render(): JSX.Element {
        return (
            <div className="check-panel-content">
                <div className="title-xs flex-row flex-center">
                    <span className="flex-grow">{Resources.RequiredYamlTemplates}</span>
                    <Button aria-label={Resources.NewTemplateButton} className="bolt-icon-button icon-only"
                        onClick={e => {
                            this._onNewTemplateClicked();
                            e.preventDefault();
                        }}>
                        <span aria-hidden="true" className="left-icon flex-noshrink fabric-icon ms-Icon--Add medium"></span>
                    </Button>
                </div>
                {this._extendsObservable.value.length == 0 &&
                    <div className="bolt-header-description body-m secondary-text">
                        {Resources.NoRequiredYamlTemplates}
                    </div>
                }
                {this._extendsObservable.value.length > 0 &&
                    <div>
                        <List
                            className="checks-list margin-vertical-4"
                            itemProvider={this._extendsObservable}
                            onActivate={this._onRowClicked}
                            singleClickActivation={true}
                            renderRow={this._renderRow}
                            width={"100%"} />
                        <div className="margin-vertical-20 flex-row flex-grow">
                            <Icon className="fabric-icon ms-Icon--Info flex-column flex-self-start margin-top-4" ariaLabel="Info" />
                            <span className="primary-text body-m margin-left-16 flex-column flex-grow scroll-hidden">Pipelines using this resource must extend at least one of the templates specified above.</span>
                        </div>
                    </div>
                }
            </div>
        );
    }

    // Source provider icons are bit strange. The GitHub icon is in the list of fabric-icons and 
    // it's black and white, so we don't need a png or svg for it. However, while the other two have
    // fabric icons, they are not correctly represented and need png/svg representations that we get
    // using the getAssetLocation method.
    private _renderIcon = (item: IExtendsCheck): JSX.Element => {
        const type = this._repositoryTypes.find(t => t.id === item.repositoryType);
        if (type) {
            // Try to get the icon asset if it is specified
            if (type.iconAssetLocation) {
                // Make sure we cache the icon asset so we don't fetch it every time
                if (!type.iconAsset) {
                    type.iconAsset = getAssetLocation(this.context.pageContext, type.iconAssetLocation);
                }

                // If we did get the icon asset, use that
                if (type.iconAsset) {
                    return (
                        <Image
                            width={24}
                            containImage={true}
                            className="icon-large-margin flex-cell"
                            src={type.iconAsset}
                            alt={type.name} />);
                }
            }

            // fall back to the icon class
            const classNames = `list-icon fabric-icon large ${type.iconClass} icon-large-margin`;
            return (<Icon className={classNames} iconName={type.name} />);
        }
        return (<div />);
    }

    private _renderRow = (index: number, item: IExtendsCheck, details: IListItemDetails<IExtendsCheck>, key?: string): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="flex-row flex-grow cursor-pointer scroll-hidden margin-vertical-8">
                    {this._renderIcon(item)}
                    <div className="list-content flex-column flex-grow scroll-hidden">
                        <div className="flex-row flex-grow flex-center">
                            <span className="body-m text-ellipsis">{item.templatePath}</span>
                        </div>
                        <span className="body-s secondary-text">{`${item.repositoryName} (${item.repositoryRef})`}</span>
                    </div>
                    {this._renderDeleteButton(index, item)}
                </div>
            </ListItem>
        );
    }

    private _renderDeleteButton = (rowIndex: number, item: IExtendsCheck): JSX.Element => {
        return (
            <div className="flex-column bolt-list-cell-content-reveal">
                <Button
                    className="reveal-on-hover"
                    subtle={true}
                    excludeTabStop={true}
                    iconProps={{
                        iconName: "Delete",
                        className: "fontSizeML"
                    }}
                    onClick={e => {
                        this._onDeleteClicked(item);
                        e.preventDefault();
                    }}
                />
            </div>
        );
    }

    private _onNewTemplateClicked = () => {
        const check: IExtendsCheck = {
            repositoryName: "",
            repositoryRef: "refs/heads/master",
            repositoryType: "git",
            templatePath: ""
        };
        const showOverlay = new ObservableValue(false);
        this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
            const onDismissPanel = async (newCheck: IExtendsCheck | undefined) => {
                if (newCheck) {
                    // Add to the list
                    this._extendsObservable.push(newCheck);
                    // Update the state
                    this._updateCheckState();
                }
                onDismiss();
            };

            return (
                <ExtendsCheckEditPanel
                    check={check}
                    isNewCheck={true}
                    onDismiss={onDismissPanel}
                    showOverlay={showOverlay}
                />
            );
        });
    }

    private _onRowClicked = (event: any, row: ITableRow<IExtendsCheck>): void => {
        if (row.data) {
            const existingCheck = row.data;
            const showOverlay = new ObservableValue(false);
            this._pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
                const onDismissPanel = async (existingCheck: IExtendsCheck | undefined) => {
                    if (existingCheck) {
                        // Update the check in the list
                        this._extendsObservable.change(row.index, existingCheck);
                        // Update the state
                        this._updateCheckState();
                    }
                    onDismiss();
                };
    
                return (
                    <ExtendsCheckEditPanel
                        check={existingCheck}
                        isNewCheck={false}
                        onDismiss={onDismissPanel}
                        showOverlay={showOverlay}
                    />
                );
            });
        }
    }

    private _onDeleteClicked = (check: IExtendsCheck): void => {
        const checks = this._extendsObservable.value;
        // Find the index of the one to delete
        const i = checks.findIndex((x, i, l) => 
            x.repositoryType === check.repositoryType &&
            x.repositoryName === check.repositoryName &&
            x.repositoryRef === check.repositoryRef &&
            x.templatePath === check.templatePath)
        // Delete the item from the list
        this._extendsObservable.splice(i, 1);
        // Update the state
        this._updateCheckState();
    }

    private _updateCheckState = (): void => {
        let shouldDisableCreateButton = this._areChecksInvalid();
        if (shouldDisableCreateButton) {
            this.props.disableCreateButton(shouldDisableCreateButton);
        }
        else {
            let settings: IExtendsCheckConfig = {
                extendsChecks: this._extendsObservable.value
            };
            let record: IExtendsCheckConfiguration = {
                type: {
                    id: extendsCheckTypeId,
                    name: extendsCheckTypeName
                },
                settings: settings,
                resource: this.props.resource,
            } as IExtendsCheckConfiguration;

            this.props.updateCheckDataAndEnableSave(record);
            this.props.updateTelemetryData(this._getTelemetryData({}));
        }
    }

    private _areChecksInvalid(): boolean {
        return this._extendsObservable.value.length === 0;
    }

    private _getTelemetryData = (settings: any): { [key: string]: string } => {
        return {
            CheckType: extendsCheckTypeId,
            Timeout: ""
        };
    }

    private _repositoryTypes: IRepositoryType[] = [
        { id: "git", name: Resources.AzureReposGit, iconClass: "ms-Icon--GitLogo", iconAssetLocation: "ms.vss-code-web/common-content/Nav-Code.png", iconAsset: undefined },
        { id: "github", name: Resources.GitHub, iconClass: "ms-Icon--GitHubLogo", iconAssetLocation: undefined, iconAsset: undefined },
        { id: "bitbucket", name: Resources.Bitbucket, iconClass: "ms-Icon--BitbucketLogo32", iconAssetLocation: "ms.vss-build-web/designer-bitbucket/Content/bitbucket.svg", iconAsset: undefined },
        { id: "githubenterprise", name: Resources.GitHubEnterprise, iconClass: "ms-Icon--GitHubLogo", iconAssetLocation: undefined, iconAsset: undefined }
    ];

    private _extendsObservable: ObservableArray<IExtendsCheck>;
    private _pageContext: IVssPageContext;
}